import { msg } from "@lingui/core/macro";
import { medicalCompetenceSchema, scheduledShiftsSchema } from "./shifts";
import { getSimplifiedRequiredCompetences } from "./activityRequirements";
import { dateSchema, timespanSchema, timestampSchema } from "./date-and-time";
import { z } from "zod";
import { listPatientSchema } from "./patients";
import type { MessageDescriptor } from "@lingui/core";
import { addressWithCoordinates } from "./addresses";

export const categorySchema = z.enum([
  "HomeVisit",
  "VideoCall",
  "ClinicVisit",
  "PatientTask",
  "AdminTask",
  "PatientMeasurementTask",
  "PhoneCall",
]);
export type IActivityCategory = z.infer<typeof categorySchema>;

// Visit categories are the activities where the patient or the employee visits the other party.
// A phone call is not considered a visit since it can be administrative or internal. The decision to exclude phone calls from the visit category might be reconsidered if needed.
export const visitCategorySchema = categorySchema.extract([
  categorySchema.enum.HomeVisit,
  categorySchema.enum.VideoCall,
  categorySchema.enum.ClinicVisit,
]);
export type IVisitCategory = z.infer<typeof visitCategorySchema>;

export const isVisitCategory = (
  category: IActivityCategory,
): category is IVisitCategory =>
  visitCategorySchema.safeParse(category).success;

// Only a home visit takes an employee to where the patient is, so only a home
// visit's planned form is a stop on a route. Grouping is a separate axis:
// occurrences of a non-routable category can be grouped without the group
// becoming route work.
export const routableCategorySchema = categorySchema.extract([
  categorySchema.enum.HomeVisit,
]);
export type IRoutableCategory = z.infer<typeof routableCategorySchema>;

export const groupableCategorySchema = categorySchema.extract([
  categorySchema.enum.HomeVisit,
  categorySchema.enum.ClinicVisit,
]);
export type IGroupableCategory = z.infer<typeof groupableCategorySchema>;

export const nonRoutableCategorySchema = categorySchema.exclude([
  ...routableCategorySchema.options,
]);

export const isRoutable = (
  category: IActivityCategory,
): category is IRoutableCategory =>
  routableCategorySchema.safeParse(category).success;

export const isGroupable = (
  category: IActivityCategory,
): category is IGroupableCategory =>
  groupableCategorySchema.safeParse(category).success;

export const hasGroupableCategory = <T extends { category: IActivityCategory }>(
  item: T,
): item is Extract<T, { category: IGroupableCategory }> =>
  isGroupable(item.category);

// A patient performs these themselves, so their occurrences carry no assignees.
const patientPerformedCategorySchema = categorySchema.extract([
  categorySchema.enum.PatientTask,
  categorySchema.enum.PatientMeasurementTask,
]);
export type IPatientPerformedCategory = z.infer<
  typeof patientPerformedCategorySchema
>;

// Only an employee-performed occurrence carries `assignees` and
// `requiredCompetences`; ask this instead of `"assignees" in occurrence`.
const employeePerformedCategorySchema = categorySchema.exclude([
  ...patientPerformedCategorySchema.options,
]);
export type IEmployeePerformedCategory = z.infer<
  typeof employeePerformedCategorySchema
>;

export const isEmployeePerformedCategory = (
  category: IActivityCategory,
): category is IEmployeePerformedCategory =>
  employeePerformedCategorySchema.safeParse(category).success;

// Generic so a caller keeps the union it passed in instead of widening to a
// shared supertype, the same way `isGroup` is. A group carries the category of
// the occurrences it groups, so a group narrows through this too.
export const isEmployeePerformed = <T extends { category: IActivityCategory }>(
  occurrenceOrGroup: T,
): occurrenceOrGroup is Extract<T, { category: IEmployeePerformedCategory }> =>
  isEmployeePerformedCategory(occurrenceOrGroup.category);

export const getActivityCategoryConfig = ({
  category,
  activityHasPatient,
}: {
  category: IActivityCategory;
  activityHasPatient: boolean;
}): {
  allowsHidden: boolean;
  allowsCompetence: boolean;
  requiresCompetence: boolean;
  requiresPatient: boolean;
  allowsInvoicingCode: boolean;
  allowsDuration: boolean;
  allowsDoubleStaffing: boolean;
  performedBy: "employee" | "patient";
} => {
  const performedBy = isEmployeePerformedCategory(category)
    ? "employee"
    : "patient";

  switch (category) {
    case categorySchema.enum.HomeVisit:
      return {
        performedBy,
        allowsHidden: activityHasPatient,
        allowsCompetence: true,
        requiresCompetence: true,
        requiresPatient: true,
        allowsInvoicingCode: activityHasPatient,
        allowsDuration: true,
        allowsDoubleStaffing: true,
      };
    case categorySchema.enum.ClinicVisit:
      return {
        performedBy,
        allowsHidden: activityHasPatient,
        allowsCompetence: true,
        requiresCompetence: true,
        requiresPatient: true,
        allowsInvoicingCode: activityHasPatient,
        allowsDuration: true,
        allowsDoubleStaffing: false,
      };
    case categorySchema.enum.VideoCall:
      return {
        performedBy,
        allowsHidden: activityHasPatient,
        allowsCompetence: true,
        requiresCompetence: true,
        requiresPatient: true,
        allowsInvoicingCode: false,
        allowsDuration: true,
        allowsDoubleStaffing: false,
      };
    case categorySchema.enum.AdminTask:
      return {
        performedBy,
        allowsHidden: false, // Is always hidden to patients in Care
        allowsCompetence: true,
        requiresCompetence: false,
        requiresPatient: false,
        allowsInvoicingCode: activityHasPatient,
        allowsDuration: true,
        allowsDoubleStaffing: false,
      };
    case categorySchema.enum.PatientTask:
      return {
        performedBy,
        allowsHidden: activityHasPatient,
        allowsCompetence: false,
        requiresCompetence: false,
        requiresPatient: true,
        allowsInvoicingCode: false,
        allowsDuration: false,
        allowsDoubleStaffing: false,
      };
    case categorySchema.enum.PatientMeasurementTask:
      return {
        performedBy,
        allowsHidden: activityHasPatient,
        allowsCompetence: false,
        requiresCompetence: false,
        requiresPatient: true,
        allowsInvoicingCode: false,
        allowsDuration: false,
        allowsDoubleStaffing: false,
      };
    case categorySchema.enum.PhoneCall:
      return {
        performedBy,
        allowsHidden: activityHasPatient,
        allowsCompetence: true,
        requiresCompetence: true,
        requiresPatient: true,
        allowsInvoicingCode: false,
        allowsDuration: true,
        allowsDoubleStaffing: false,
      };
    default:
      throw category satisfies never;
  }
};

export const videoTypeSchema = z.enum(["Round", "DigitalVisit"]);

export type IVideoType = z.infer<typeof videoTypeSchema>;

export const activityStatusSchema = z.enum(["active", "closed"]);
export type IActivityStatus = z.infer<typeof activityStatusSchema>;

const visitStatuses = [
  "planned",
  "travellingTo",
  "ongoing",
  "finished",
] as const;
export const visitStatusSchema = z.enum(visitStatuses);

// A group of home visits becomes a visit when it is routed, and cancelling the
// group is where that stops: a cancelled group is off the planning board, so it
// is never a stop on a route and never waiting to be planned. Cancelled is
// therefore a status a group can carry and a visit cannot, which is why a group
// has its own status even though the wire calls the field `visitStatus`.
const groupStatuses = [...visitStatuses, "cancelled"] as const;
export const groupStatusSchema = z.enum(groupStatuses);
export type IGroupStatus = z.infer<typeof groupStatusSchema>;

// Clinic visits have no transport leg and their execution is not timed, so a
// clinic-visit group can never be travelling to or ongoing.
export const clinicVisitGroupStatusSchema = groupStatusSchema.extract([
  "planned",
  "finished",
  "cancelled",
]);

// Marking as finished and reverting are the two ends of the same axis, so each
// one's input statuses are derived from the schema rather than spelled out: a
// renamed status is then a compile error at every call site instead of a
// silently widened type.
const unfinishedVisitStatusSchema = visitStatusSchema.exclude([
  visitStatusSchema.enum.finished,
]);
export type IUnfinishedVisitStatus = z.infer<
  typeof unfinishedVisitStatusSchema
>;

const revertableVisitStatusSchema = visitStatusSchema.extract([
  visitStatusSchema.enum.travellingTo,
  visitStatusSchema.enum.ongoing,
  visitStatusSchema.enum.finished,
]);
export type IRevertableVisitStatus = z.infer<
  typeof revertableVisitStatusSchema
>;

// A cancelled group is off the planning board, so it is no more finishable than
// an already finished one.
const finishableGroupStatusSchema = groupStatusSchema.exclude([
  groupStatusSchema.enum.finished,
  groupStatusSchema.enum.cancelled,
]);
export type IFinishableGroupStatus = z.infer<
  typeof finishableGroupStatusSchema
>;

export const timeOfDaySchema = z.enum(["Specific", "Any"]);
export type ITimeOfDay = z.infer<typeof timeOfDaySchema>;
export const timeOfDayDictionary = {
  [timeOfDaySchema.enum.Specific]: {
    long: msg`Vid specifik tid`,
    short: msg`Specifik tid`,
  } as const,
  [timeOfDaySchema.enum.Any]: {
    long: msg`När som helst under dagen`,
    short: msg`Under dagen`,
  } as const,
};

const orderedStatuses = [
  "expired",
  "notReady",
  "ready",
  "ongoing",
  "finished",
] as const;

export const orderedCategories = [
  categorySchema.enum.HomeVisit,
  categorySchema.enum.ClinicVisit,
  categorySchema.enum.VideoCall,
  categorySchema.enum.PhoneCall,
  categorySchema.enum.AdminTask,
  categorySchema.enum.PatientTask,
  categorySchema.enum.PatientMeasurementTask,
] as const;

export const activityOccurrenceStatusSchema = z.enum(orderedStatuses);
export type IActivityOccurrenceStatus = z.infer<
  typeof activityOccurrenceStatusSchema
>;

const unfinishedActivityOccurrenceStatusSchema =
  activityOccurrenceStatusSchema.exclude([
    activityOccurrenceStatusSchema.enum.finished,
  ]);
export type IUnfinishedActivityOccurrenceStatus = z.infer<
  typeof unfinishedActivityOccurrenceStatusSchema
>;

const readyStatuses = ["ready", "ongoing", "finished", "expired"] as const;
const readyActivityOccurrenceStatusSchema = z.enum(readyStatuses);

export const activityOccurrenceStatusDictionary: Record<
  IActivityOccurrenceStatus,
  MessageDescriptor
> = {
  notReady: msg`Att planera`,
  ready: msg`Planerat`,
  ongoing: msg`Pågående`,
  finished: msg`Utfört`,
  expired: msg`Ej utfört`,
};

export const getActivityOccurrenceStatusTimestamp = (
  occurrence: Pick<IActivityOccurrence, "status" | "finishedAt">,
) => {
  const { status, finishedAt } = occurrence;

  if (status === "finished" && finishedAt) {
    return finishedAt;
  }

  return undefined;
};

// A group carries its status as `visitStatus`; the same group delivered inside a
// route carries it as `status`, and there it is a visit, which cannot be
// cancelled. A caller holding either shape reads it here instead of probing for
// the property, and gets back the wider of the two.
export const getGroupOrVisitStatus = (groupOrVisit: {
  visitStatus?: IGroupStatus | null;
  status?: z.infer<typeof visitStatusSchema> | null;
}): IGroupStatus | undefined =>
  groupOrVisit.visitStatus ?? groupOrVisit.status ?? undefined;

export const getGroupStatusTimestamp = (group: {
  visitStatus?: IGroupStatus | null;
  startedTravellingToAt?: Date | null;
  startedAt?: Date | null;
  occurrences: { finishedAt?: Date | null }[];
}): Date | undefined => {
  if (group.visitStatus === "travellingTo" && group.startedTravellingToAt) {
    return group.startedTravellingToAt;
  }

  if (group.visitStatus === "ongoing" && group.startedAt) {
    return group.startedAt;
  }

  if (group.visitStatus === "finished") {
    return group.occurrences.reduce<Date | undefined>(
      (latest, { finishedAt }) =>
        finishedAt && (!latest || finishedAt > latest) ? finishedAt : latest,
      undefined,
    );
  }

  return undefined;
};

export const orderedMeasurementsList = [
  "bloodPressure",
  "pulse",
  "saturation",
  "temperature",
  "weight",
  "bloodGlucose",
] as const;
export const measurementSchema = z.enum(orderedMeasurementsList);

export type IMeasurementsType = z.infer<typeof measurementSchema>;

export const measurementsDictionary: Record<
  | "bloodPressure"
  | "pulse"
  | "saturation"
  | "temperature"
  | "weight"
  | "bloodGlucose",
  MessageDescriptor
> = {
  bloodPressure: msg`Blodtryck`,
  pulse: msg`Puls`,
  saturation: msg`Syresättning`,
  temperature: msg`Temperatur`,
  weight: msg`Vikt`,
  bloodGlucose: msg`Blodsocker`,
};

// Every field but the patient, which lives in a separate host. The per-category
// schemas below add it; activities.unenriched.ts has them with a patient id.
const activityOccurrenceBaseSchema = z.object({
  activityId: z.uuid(),
  category: categorySchema,
  description: z.string().nullish(),
  note: z.string().nullish(),
  duration: z.number(),
  end: dateSchema,
  finishedAt: timestampSchema,
  hidden: z.boolean(),
  id: z.string(),
  recurring: z.boolean(),
  start: dateSchema,
  timespan: timespanSchema,
  timeOfDay: timeOfDaySchema,
  title: z.string(),
});

// Standardized extenders
const assignees = { assignees: scheduledShiftsSchema };
// Read models expose requiredCompetences in canonical form: the raw set from the wire
// is de-duplicated and hierarchy-simplified at parse time, so every display and
// fulfillment consumer sees the same simplified set.
const requiredCompetences = {
  requiredCompetences: z
    .array(medicalCompetenceSchema)
    .transform(getSimplifiedRequiredCompetences),
};
const anyStatus = { status: activityOccurrenceStatusSchema };
const readyStatus = { status: readyActivityOccurrenceStatusSchema };

// Standardized omitters
const listPatient = {
  patient: listPatientSchema,
} as const;
const nullishListPatient = {
  patient: listPatientSchema.nullish(),
} as const;
const patientAndAssignees = {
  assignees: true,
  patient: true,
} as const;

// A group carries `occurrences` and a single occurrence does not, in every shape
// that models one: with or without a patient, invoicing rows, and the visit a
// group becomes inside a route. Generic so a caller keeps the union it passed in
// instead of widening to a shared supertype.
export const isGroup = <T extends object>(
  occurrenceOrGroup: T,
): occurrenceOrGroup is Extract<T, { occurrences: unknown }> =>
  "occurrences" in occurrenceOrGroup;

export const getUniqueId = (occurrenceOrGroup: IActivityOccurrenceOrGroup) => {
  return isGroup(occurrenceOrGroup)
    ? occurrenceOrGroup.id
    : `${occurrenceOrGroup.activityId}:${occurrenceOrGroup.id}`;
};

export const homeVisitActivityOccurrenceSchema =
  activityOccurrenceBaseSchema.extend({
    ...listPatient,
    ...requiredCompetences,
    ...anyStatus,
    ...assignees,
    category: z.literal(categorySchema.enum.HomeVisit),
    doubleStaffing: z.boolean(),
  });

// An occurrence as its container sees it. A group and the visit it becomes both
// hold the patient and the assignees once, for all the occurrences they carry,
// so a grouped occurrence does not repeat either.
export const groupedHomeVisitActivityOccurrenceSchema =
  homeVisitActivityOccurrenceSchema.omit(patientAndAssignees);

export type IGroupedHomeVisitActivityOccurrence = z.infer<
  typeof groupedHomeVisitActivityOccurrenceSchema
>;

// A group collects occurrences of one category. Category-specific schemas add
// their literal and occurrence shape; routable fields stay exclusive to home
// visits.
const groupBaseSchema = z.object({
  id: z.string(),
  start: dateSchema,
  end: dateSchema,
  timeOfDay: timeOfDaySchema,
  visitStatus: groupStatusSchema.nullish(),
  visitCancellationReason: z.string().nullish(),
  ...requiredCompetences,
  ...assignees,
});

export const groupOfHomeVisitActivityOccurrenceSchema = groupBaseSchema.extend({
  category: z.literal(categorySchema.enum.HomeVisit),
  occurrences: z.array(homeVisitActivityOccurrenceSchema),
  ...listPatient,
  // A home-visit group is a stop on a route, so it carries travel and visit
  // timestamps as well as its double-staffing requirement.
  startedTravellingToAt: timestampSchema,
  startedAt: timestampSchema,
  doubleStaffing: z.boolean(),
});
export type IGroupOfHomeVisitActivityOccurrence = z.infer<
  typeof groupOfHomeVisitActivityOccurrenceSchema
>;

export const videoActivityOccurrenceSchema =
  activityOccurrenceBaseSchema.extend({
    ...assignees,
    ...listPatient,
    ...requiredCompetences,
    ...anyStatus,
    category: z.literal(categorySchema.enum.VideoCall),
    type: videoTypeSchema,
  });

export const clinicVisitActivityOccurrenceSchema =
  activityOccurrenceBaseSchema.extend({
    ...assignees,
    ...listPatient,
    ...requiredCompetences,
    ...anyStatus,
    category: z.literal(categorySchema.enum.ClinicVisit),
    address: addressWithCoordinates.nullish(),
  });

export const groupOfClinicVisitActivityOccurrenceSchema =
  groupBaseSchema.extend({
    category: z.literal(categorySchema.enum.ClinicVisit),
    occurrences: z.array(clinicVisitActivityOccurrenceSchema),
    ...listPatient,
    visitStatus: clinicVisitGroupStatusSchema.nullish(),
  });
export const groupOfActivityOccurrenceSchema = z.discriminatedUnion(
  "category",
  [
    groupOfHomeVisitActivityOccurrenceSchema,
    groupOfClinicVisitActivityOccurrenceSchema,
  ],
);
export type IGroupOfActivityOccurrence = z.infer<
  typeof groupOfActivityOccurrenceSchema
>;

export const phoneCallActivityOccurrenceSchema =
  activityOccurrenceBaseSchema.extend({
    ...assignees,
    ...listPatient,
    ...requiredCompetences,
    ...anyStatus,
    category: z.literal(categorySchema.enum.PhoneCall),
  });

export const patientTaskActivityOccurrenceSchema =
  activityOccurrenceBaseSchema.extend({
    category: z.literal(categorySchema.enum.PatientTask),
    ...listPatient,
    ...readyStatus,
  });

// AdminTask is the one category that may have no patient at all, so its patient
// is nullish on both sides of the enrichment.
export const adminTaskOccurrenceSchema = activityOccurrenceBaseSchema.extend({
  ...assignees,
  ...nullishListPatient,
  ...requiredCompetences,
  ...anyStatus,
  category: z.literal(categorySchema.enum.AdminTask),
});

const adminTaskOccurrenceWithRequiredPatientSchema =
  adminTaskOccurrenceSchema.extend(listPatient);

export const patientMeasurementTaskActivityOccurrenceSchema =
  activityOccurrenceBaseSchema.extend({
    ...listPatient,
    ...readyStatus,
    category: z.literal(categorySchema.enum.PatientMeasurementTask),
    measurements: z.array(measurementSchema),
  });

const activityOccurrenceWithRequiredPatientSchema = z.discriminatedUnion(
  "category",
  [
    homeVisitActivityOccurrenceSchema,
    videoActivityOccurrenceSchema,
    clinicVisitActivityOccurrenceSchema,
    patientTaskActivityOccurrenceSchema,
    adminTaskOccurrenceWithRequiredPatientSchema,
    patientMeasurementTaskActivityOccurrenceSchema,
    phoneCallActivityOccurrenceSchema,
  ],
);
export type IActivityOccurrenceWithRequiredPatient = z.infer<
  typeof activityOccurrenceWithRequiredPatientSchema
>;

const activityOccurrenceOrGroupWithRequiredPatientSchema = z.union([
  activityOccurrenceWithRequiredPatientSchema,
  groupOfActivityOccurrenceSchema,
]);
export type IActivityOccurrenceOrGroupWithRequiredPatient = z.infer<
  typeof activityOccurrenceOrGroupWithRequiredPatientSchema
>;

const nonRoutableEmployeePerformedActivityOccurrenceSchema =
  z.discriminatedUnion("category", [
    videoActivityOccurrenceSchema,
    clinicVisitActivityOccurrenceSchema,
    adminTaskOccurrenceSchema,
    phoneCallActivityOccurrenceSchema,
  ]);
export type INonRoutableEmployeePerformedActivityOccurrence = OccurrenceOf<
  Exclude<IEmployeePerformedCategory, IRoutableCategory>
>;

// Non-routable work can appear directly on a shift either as a standalone
// occurrence or, for groupable clinic visits, as a group.
export const nonRoutableEmployeePerformedActivityOccurrenceOrGroupSchema =
  z.union([
    nonRoutableEmployeePerformedActivityOccurrenceSchema,
    groupOfClinicVisitActivityOccurrenceSchema,
  ]);
export type INonRoutableEmployeePerformedActivityOccurrenceOrGroup = z.infer<
  typeof nonRoutableEmployeePerformedActivityOccurrenceOrGroupSchema
>;

export const activityOccurrenceSchema = z.discriminatedUnion("category", [
  homeVisitActivityOccurrenceSchema,
  videoActivityOccurrenceSchema,
  clinicVisitActivityOccurrenceSchema,
  patientTaskActivityOccurrenceSchema,
  adminTaskOccurrenceSchema,
  patientMeasurementTaskActivityOccurrenceSchema,
  phoneCallActivityOccurrenceSchema,
]);
export type IActivityOccurrence = z.infer<typeof activityOccurrenceSchema>;

// The fields every occurrence variant has, as one flat object rather than a
// union. A patient-performed occurrence carries no assignees, so `assignees` is
// not among them — which is what a component rendering any occurrence, from any
// source, can rely on.
export type ISharedActivityOccurrenceFields = Pick<
  IActivityOccurrence,
  keyof IActivityOccurrence
>;

// Narrows the occurrence union to one category or a union of categories.
export type OccurrenceOf<
  C extends IActivityCategory,
  TOccurrence extends { category: IActivityCategory } = IActivityOccurrence,
> = Extract<TOccurrence, { category: C }>;

// Not discriminated: a group shares its occurrences' category, so only
// `occurrences` (see isGroup) tells them apart.
const activityOccurrenceOrGroupSchema = z.union([
  activityOccurrenceSchema,
  groupOfActivityOccurrenceSchema,
]);
export type IActivityOccurrenceOrGroup = z.infer<
  typeof activityOccurrenceOrGroupSchema
>;
const _activityOccurrencesAndGroupsSchema = z.array(
  activityOccurrenceOrGroupSchema,
);
export type IActivityOccurrencesAndGroups = z.infer<
  typeof _activityOccurrencesAndGroupsSchema
>;

export const activityRequirementStatusSchema = z.enum([
  "neutral",
  "unfulfilled",
  "unassigned",
  "assigned",
]);
export type IActivityRequirementStatus = z.infer<
  typeof activityRequirementStatusSchema
>;

// Cancelling a group takes it off the planning board, so nothing waiting to be
// planned carries a cancelled status: what the unplanned list delivers is a
// group that still stands to be assigned, and it is read as carrying its active
// status rather than the wider group one.
const unplannedVisitStatus = {
  visitStatus: visitStatusSchema.nullish(),
};
export const unplannedHomeVisitGroupSchema =
  groupOfHomeVisitActivityOccurrenceSchema.extend(unplannedVisitStatus);
export const unplannedClinicVisitGroupSchema =
  groupOfClinicVisitActivityOccurrenceSchema.extend({
    visitStatus: clinicVisitGroupStatusSchema
      .exclude([groupStatusSchema.enum.cancelled])
      .nullish(),
  });
const unplannedGroupSchema = z.union([
  unplannedHomeVisitGroupSchema,
  unplannedClinicVisitGroupSchema,
]);

// A single occurrence carries no visit status at all, so the unplanned list is
// the same union as any occurrence list, with only the group read narrowly.
const unplannedOccurrenceOrGroupSchema = z.union([
  unplannedGroupSchema,
  ...activityOccurrenceSchema.options,
]);
export type IUnplannedOccurrenceOrGroup = z.infer<
  typeof unplannedOccurrenceOrGroupSchema
>;
